import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import { DocumentFilters } from 'src/app/models/document';
import { DocumentMetadata, MetadataType } from 'src/app/models/documentMetadata';
import { ProductService } from 'src/app/models/productService';
import { Trademark } from 'src/app/models/trademark';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ProductServiceService } from 'src/app/services/product-service.service';
import { TrademarkService } from 'src/app/services/trademark.service';

interface TrademarkWithClassesAndPS {
  ref: string;
  name: string;
  identifierNumber: string;
  countryOfDesignation: string;
  confidence?: number;
  expanded?: boolean;
  classes?: ClassWithPS[]
}

interface ClassWithPS {
  classNumber: number;
  confidence: number;
  expanded?: boolean;
  terms: (ProductService & { confidence: number })[]
}

@Component({
  selector: 'app-document-products-services',
  templateUrl: './document-products-services.component.html',
  styleUrls: ['./document-products-services.component.scss']
})
export class DocumentProductsServicesComponent {
  @Input() documentId?: string;
  @Input() metadata?: DocumentMetadata[] = [];
  @Input() trademarks: TrademarkWithClassesAndPS[] = [];
  _trademarks: TrademarkWithClassesAndPS[] = [];
  @Input() productsServices?: {
    ref: string;
    label?: string;
    classNumber: number;
    trademark: string;
  }[] = [];
  @Input() terms?: ProductService[] = [];
  @Input() shown: boolean = false;
  @Input() trademarkFilter?: Trademark[];
  @Input() niceClassFilter?: number[];
  @Input() productServicesFilter?: ProductService[];
  limit: number = 3;

  focused: boolean = false;
  @Output() focus = new EventEmitter<boolean>();

  user?: User;


  constructor(
    private authService: AuthenticationService,
    private productServiceService: ProductServiceService,
    private trademarkService: TrademarkService,
    private router: Router
  ) {
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
    });
  }

  ngOnInit(): void {
    this.populateConfidence();
    this.populateClassesAndPS();
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['trademarks'] && !_.isEqual(changes['trademarks']['currentValue'], changes['trademarks']['previousValue']))
      || (changes['productsServices'] && !_.isEqual(changes['productsServices']['currentValue'], changes['productsServices']['previousValue']))
      || (changes['metadata'] && !_.isEqual(changes['metadata']['currentValue'], changes['metadata']['previousValue'])
        || (changes['trademarkFilter'] && !_.isEqual(changes['trademarkFilter']['currentValue'], changes['trademarkFilter']['previousValue']))
        || (changes['niceClassFilter'] && !_.isEqual(changes['niceClassFilter']['currentValue'], changes['niceClassFilter']['previousValue']))
        || (changes['productServicesFilter'] && !_.isEqual(changes['productServicesFilter']['currentValue'], changes['productServicesFilter']['previousValue'])))
    ) {
      this.populateConfidence();
      this.populateClassesAndPS();
    }
  }

  populateConfidence() {
    this.trademarks.forEach(trademark => {
      trademark.confidence = this.getConfidence(trademark)
    })
  }

  async populateClassesAndPS() {
    if (this.documentId) {
      const trademarksTerms: ProductService[] = await this.productServiceService.retrieveAllForDocument(this.documentId);
      const documentTrademarks: Trademark[] = await this.trademarkService.retrieveAllForDocument(this.documentId);
      this._trademarks = await Promise.all(this.trademarks.filter((tm) => this.filterTrademark(tm)).map(async (trademark) => {
        let trademarkTerms: ProductService[] = [];

        trademarkTerms = trademarksTerms.filter(ps => ps.trademark?.ref === trademark.ref)

        trademark.expanded = false;
        trademark.classes = [];
        const documentTrademark = documentTrademarks.find((tm) => tm._id === trademark.ref);
        if (documentTrademark && documentTrademark.niceClassification) {
          this.metadata?.filter((datum) => datum.type === MetadataType.NICE_CLASS).forEach((datum) => {
            if (documentTrademark.niceClassification?.map(niceClass => niceClass.classNumber).includes(parseInt(datum.value)) && this.filterNiceClass(parseInt(datum.value))) {
              trademark.classes?.push({
                classNumber: parseInt(datum.value),
                confidence: this.getClassConfidence(parseInt(datum.value)),
                expanded: false,
                terms: []
              })
            }
          })
        }

        this.metadata?.filter((datum) => datum.type === MetadataType.PRODUCT_SERVICE).forEach((datum) => {
          const termId = datum.value.split("_")[0];
          const trademarkId = datum.value.split("_")[1];
          const classNumber = parseInt(datum.value.split("_")[2]);
          const term = trademarkTerms.find(t => t._id === termId && t.trademark?.ref === trademark.ref && t.classNumber === classNumber);
          const existingClass = trademark.classes?.find(c => c.classNumber === classNumber);
          if (term && this.filterProductService(term)) {
            if (existingClass && this.filterNiceClass(existingClass.classNumber)) {
              existingClass.terms.push({ ...term, confidence: datum.confidence * 100 })
            } else if (this.filterNiceClass(classNumber)) {
              trademark.classes?.push({
                classNumber,
                confidence: this.getClassConfidence(classNumber),
                expanded: false,
                terms: [{ ...term, confidence: datum.confidence * 100 }]
              })
            }
          }
        })
        return trademark;
      }));
    }
  }

  filterTrademark(trademark: Trademark) {
    if (!this.trademarkFilter || this.trademarkFilter.length == 0) {
      return true;
    } else {
      return trademark && this.trademarkFilter.map(t => t.identifierNumber).includes(trademark.identifierNumber);
    }
  }

  filterNiceClass(niceClass: number) {
    if (!this.niceClassFilter || this.niceClassFilter.length == 0) {
      return true;
    } else {
      return this.niceClassFilter.includes(niceClass);
    }
  }

  filterProductService(ps: ProductService) {
    if (!this.productServicesFilter || this.productServicesFilter.length == 0) {
      return true;
    } else {
      return ps && this.productServicesFilter.map(p => p._id).includes(ps._id);
    }
  }

  getConfidence(trademark: {
    ref: string;
    name: string;
    identifierNumber: string;
    countryOfDesignation: string;
  }) {
    return (this.metadata?.find(datum => datum.type === MetadataType.TRADEMARK && datum.value === `${trademark.ref}_${trademark.name || ''}_${trademark.identifierNumber}_${trademark.countryOfDesignation}`)?.confidence ?? 0) * 100
  }

  getClassConfidence(niceClass: number) {
    return (this.metadata?.find(datum => datum.type === MetadataType.NICE_CLASS && datum.value === `${niceClass}`)?.confidence ?? 0) * 100
  }

  toggle() {
    if (this.limit === 3) {
      this.limit = this.trademarks.length;
    } else {
      this.limit = 3;
    }
  }


  toTrademark(id: string | undefined) {
    if (id) {
      this.router.navigate(['trademarks', id], {
        state: {
          from: "DOCUMENT"
        }
      })
    }
  }

  enterFocus() {
    this.focused = true;
    this.focus.emit(true);
  }

  leaveFocus() {
    this.focused = false;
    this.focus.emit(false);
  }

  getProductServiceLabel(productService: ProductService) {
    return this.productServiceService.getLabel(productService, this.user?.lang);
  }

  hasDetectedNiceClasses(trademark: TrademarkWithClassesAndPS) {
    return trademark.classes && trademark.classes.length > 0;
  }

  hasDetectedPS(niceClass: ClassWithPS) {
    return niceClass.terms && niceClass.terms.length > 0;
  }

  hasFilters(): boolean {
    return (this.niceClassFilter && this.niceClassFilter.length > 0
      || this.trademarkFilter && this.trademarkFilter.length > 0
      || this.productServicesFilter && this.productServicesFilter.length > 0
    ) ? true : false;
  }
}
